<template>
  <div>
    <!-- Port Shutdown Table -->
    <table class="table align-items-center mb-0">
      <thead>
        <tr>
          <th class="text-sm">Interface</th>
          <th class="text-sm">Enable</th>
          <th class="text-sm">Speed</th>
          <th class="text-sm">Duplex</th>
          <th class="text-sm">Flow</th>
          <th class="text-sm">Medium</th>
          <th class="text-sm">Fiber Auto</th>
          <th class="text-sm">Switch</th>
        </tr>
      </thead>
      <tbody>
        <template v-if="loading">
          <tr>
            <td colspan="8">
              <Skalaton />
            </td>
          </tr>
        </template>
        <template v-else-if="portUpdateValue?.ports && portUpdateValue.ports.length">
          <tr
            v-for="(port, index) in portUpdateValue.ports"
            :key="index"
          >
            <td class="text-sm font-weight-bold">{{ getInterfaceWithDescription(port.Interface) }}</td>
            <td class="text-sm">
              <span
                class="badge"
                :class="getEnableClass(port.Enable)"
                style="text-transform: uppercase; color: rgb(255 249 249) !important;"
              >
                {{ port.Enable || 'N/A' }}
              </span>
            </td>
            <td class="text-sm">{{ port.Speed || 'N/A' }}</td>
            <td class="text-sm">{{ port.Duplex || 'N/A' }}</td>
            <td class="text-sm">
              <span
                class="badge"
                :class="getFlowClass(port.Flow)"
                style="text-transform: uppercase; color: rgb(255 249 249) !important;"
              >
                {{ port.Flow || 'N/A' }}
              </span>
            </td>
            <td class="text-sm">{{ port.medium || 'N/A' }}</td>
            <td class="text-sm">
              <span
                class="badge"
                :class="getFiberAutoClass(port.fiberAuto)"
                style="text-transform: uppercase; color: rgb(255 249 249) !important;"
              >
                {{ port.fiberAuto || 'N/A' }}
              </span>
            </td>
            <td class="text-sm">
              <div class="d-flex align-items-center justify-content-center">
                <label class="switch-toggle">
                  <input
                    type="checkbox"
                    :checked="port.Enable === 'up'"
                    :disabled="togglingPorts[port.intfenable] || !port.intfenable"
                    @change="togglePort(port)"
                  />
                  <span class="slider" :class="{ 'disabled': togglingPorts[port.intfenable] || !port.intfenable }"></span>
                </label>
                <span v-if="togglingPorts[port.intfenable]" class="ms-2">
                  <i class="fas fa-spinner fa-spin text-primary"></i>
                </span>
              </div>
            </td>
          </tr>
        </template>
        <template v-else>
          <tr>
            <td colspan="8" class="text-center">No Port Shutdown data available</td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>

<script>
import Skalaton from '@/components/Skalaton.vue';
import { useSwitchDetailsBdcomSwitchApi } from '@/services/switch-details-bdcom-swicth.service';
import showSwal from '@/mixins/showSwal';

export default {
  name: 'PortUpdateVlaueTable',
  components: {
    Skalaton
  },
  props: {
    portUpdateValue: {
      type: Object,
      default: () => ({})
    },
    basicInfo: {
      type: Object,
      required: false,
      default: () => ({})
    },
    loading: {
      type: Boolean,
      default: false
    },
    switchId: {
      type: [String, Number],
      required: true
    },
    portDescriptions: {
      type: Object,
      required: false,
      default: () => null
    }
  },
  data() {
    return {
      togglingPorts: {}
    };
  },
  methods: {
    getEnableClass(status) {
      if (status === 'up') return 'bg-success';
      if (status === 'down') return 'bg-danger';
      return 'bg-secondary';
    },
    
    getFlowClass(status) {
      if (status === 'Off') return 'bg-secondary';
      if (status === 'On') return 'bg-success';
      return 'bg-warning';
    },
    
    getFiberAutoClass(status) {
      if (status === 'On') return 'bg-success';
      if (status === 'Off') return 'bg-secondary';
      return 'bg-warning';
    },

    getInterfaceWithDescription(interfaceName) {
      if (!interfaceName) return 'N/A';
      
      if (!this.portDescriptions || !this.portDescriptions.ports || !Array.isArray(this.portDescriptions.ports)) {
        return interfaceName;
      }
      
      const port = this.portDescriptions.ports.find(p => p.Interface === interfaceName);
      
      if (port && port.description && port.description.trim() !== '') {
        return `${interfaceName} - ${port.description}`;
      }
      
      return interfaceName;
    },

    async togglePort(port) {
      if (!port.intfenable) {
        showSwal.methods.showSwal({
          type: 'error',
          message: 'Port interface enable value not available',
        });
        return;
      }     

      // Store all values before async operation to avoid race condition
      const portIntfenable = port.intfenable;
      const currentEnableStatus = port.Enable;
      const currentStatus = currentEnableStatus === 'up' ? 'up' : 'down';
      const newStatus = currentStatus === 'up' ? 'down' : 'up';

      // Set loading state for this specific port
      this.togglingPorts[portIntfenable] = true;

      try {
        await useSwitchDetailsBdcomSwitchApi().getSwitchOnOffVlaue(this.switchId, portIntfenable, newStatus);
        
        // Update the port's Enable status locally using the stored newStatus value
        // eslint-disable-next-line require-atomic-updates
        port.Enable = newStatus;
        
        showSwal.methods.showSwal({
          type: 'success',
          message: `Port ${port.Interface} switched ${newStatus === 'up' ? 'ON' : 'OFF'} successfully`,
        });
      } catch (error) {
        console.error('Error toggling port:', error);
        showSwal.methods.showSwal({
          type: 'error',
          message: `Failed to toggle port ${port.Interface}`,
        });
      } finally {
        this.togglingPorts[portIntfenable] = false;
      }
    }
  }
}
</script>

<style scoped>
/* Toggle Switch Styles */
.switch-toggle {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
  margin: 0;
}

.switch-toggle input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.4s;
  border-radius: 24px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.4s;
  border-radius: 50%;
}

input:checked + .slider {
  background-color: #82D616;
}

input:focus + .slider {
  box-shadow: 0 0 1px #82D616;
}

input:checked + .slider:before {
  transform: translateX(26px);
}

.slider.disabled {
  background-color: #e0e0e0;
  cursor: not-allowed;
  opacity: 0.6;
}

input:disabled + .slider {
  background-color: #e0e0e0;
  cursor: not-allowed;
  opacity: 0.6;
}
</style>

